import React, { useMemo, useState } from 'react';

import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Typography } from '@material-ui/core';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import CustomAccordion from 'components/CustomAccordion';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { InstanceActivityLogEntry } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';

type InstanceStatusLogAccordionProps = {
  instanceId: number;
};

// Message format: "2026-02-24T12:41:26+01:00 - Delegated by ..." — extract date and content
const MESSAGE_WITH_LEADING_DATE = /^(\d{4}-\d{2}-\d{2}T[\d:.+Z-]+)\s+-\s+(.*)$/;

const parseMessage = (message: string | undefined, fallbackCreatedAt: string): { dateStr: string; content: string } => {
  if (!message || !message.trim()) {
    return { dateStr: fallbackCreatedAt, content: '—' };
  }
  const match = message.trim().match(MESSAGE_WITH_LEADING_DATE);
  if (match) {
    return { dateStr: match[1], content: match[2].trim() || '—' };
  }
  return { dateStr: fallbackCreatedAt, content: message };
};

const formatDate = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), 'yyyy-MM-dd HH:mm');
  } catch {
    return dateStr;
  }
};

type DateSortOrder = 'asc' | 'desc';

const InstanceStatusLogAccordion: React.FC<InstanceStatusLogAccordionProps> = ({ instanceId }) => {
  const { t } = useTranslation();
  const [dateSortOrder, setDateSortOrder] = useState<DateSortOrder>('desc');

  const { data, isLoading, isError } = useQuery(
    ['ACTION_LIBRARY_INSTANCE_STATUS_LOGS', instanceId],
    () => actionLibraryInstancesApi.getInstanceStatusLogs(instanceId, { limit: 500 }),
    { enabled: instanceId > 0 },
  );

  const rawEntries: InstanceActivityLogEntry[] = data?.data?.results ?? [];
  const logCount = rawEntries.length;

  const sortedEntries = useMemo(
    () =>
      [...rawEntries].sort((a, b) => {
        const { dateStr: dateA } = parseMessage(a.message, a.createdAt);
        const { dateStr: dateB } = parseMessage(b.message, b.createdAt);
        const timeA = new Date(dateA).getTime();
        const timeB = new Date(dateB).getTime();
        return dateSortOrder === 'desc' ? timeB - timeA : timeA - timeB;
      }),
    [rawEntries, dateSortOrder],
  );

  const handleDateSortClick = () => {
    setDateSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'));
  };

  const content = () => {
    if (isError) {
      return (
        <Typography color='textSecondary' variant='body2'>
          {t(action_library_messages.errors.something_went_wrong_try_later)}
        </Typography>
      );
    }

    if (isLoading && rawEntries.length === 0) {
      return (
        <Typography color='textSecondary' variant='body2'>
          {t(general_messages.loading)}
        </Typography>
      );
    }

    if (rawEntries.length === 0) {
      return (
        <Typography color='textSecondary' variant='body2'>
          {t(action_library_messages.instance.detail.action_log_empty)}
        </Typography>
      );
    }

    return (
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell sortDirection={dateSortOrder}>
              <TableSortLabel active direction={dateSortOrder} onClick={handleDateSortClick}>
                {t(action_library_messages.instance.detail.action_log_date)}
              </TableSortLabel>
            </TableCell>
            <TableCell>{t(action_library_messages.instance.detail.action_log_change)}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEntries.map(log => {
            const { dateStr, content: messageContent } = parseMessage(log.message, log.createdAt);
            return (
              <TableRow key={`${log.id}-${log.createdAt}`}>
                <TableCell>{formatDate(dateStr)}</TableCell>
                <TableCell>{messageContent}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <CenteredGrid
      component={CustomAccordion}
      heading={
        <Typography variant='h6'>
          {t(action_library_messages.instance.detail.action_log)} ({logCount})
        </Typography>
      }
      id='instance-status-log'
      width='lg'
    >
      {content()}
    </CenteredGrid>
  );
};

export default InstanceStatusLogAccordion;
